import { Addressed, keyFor } from '../../../utils/how-somebody-is-addressed.util';
import { CardSide, scoreLabel } from '../../events/services/what-a-card-shows-of-ratings';

/**
 * What the one line of a member says: « Camille D. » in bold, and under it
 * « Lyon · ★ 4,8 · 12 avis ». Maude voted version A, « La ligne », on
 * 20 October 2026, for the page of an outing (organiser and participants).
 *
 * NOTHING UNDER THREE VOICES, the rule of the profile card. The API already
 * answers no score under three; the line checks the count again, so a score
 * that arrives beside a count of two is not drawn. Under three, the line says
 * « Pas encore notée », « Pas encore noté » or « Pas encore noté·e »: the form
 * of THAT member, sent by the API, as for the rank chip Maude accepted the
 * same day. A form that does not come agrees with nobody.
 *
 * AN ABSENT FIELD IS A PART NOT DRAWN. A published build talks to whichever
 * API is deployed: an API older than this answers the score alone, without
 * the count, the town or the form. Then the line shows the score and leaves
 * the rest out. No answer at all for a person (a call that failed, or a block
 * between the two) leaves the line with the name only.
 *
 * MYSELF. A tap on my own line opens my Profile page, not a member page about
 * me; a tap on anybody else's opens their member page.
 */
export const MIN_VOICES_FOR_A_SCORE = 3;

export interface PersonOnALine {
  id?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
}

/** What the API said of one person on one side, any field possibly absent. */
export interface WhatWasSaidOfThem {
  score?: number | null;
  count?: number;
  city?: string;
  form?: string;
}

export interface LineWords {
  say: (key: string, params?: Record<string, unknown>) => string;
  language: string;
}

export interface ProfileLine {
  /** Two letters, drawn when there is no photograph. */
  initials: string;
  photo?: string;
  city?: string;
  /** « 4,8 » or "4.8". */
  score?: string;
  /** « 12 avis », "12 reviews". Only beside a score. */
  reviews?: string;
  /** « Pas encore notée », in place of the score. */
  notYetRated?: string;
  isMe: boolean;
  /** Where a tap goes; empty when it goes nowhere. */
  opens: string[];
}

const FORMS: readonly Addressed[] = ['female', 'male', 'neutral'];

/**
 * One side of the answer of `/events/:id/ratings/scores` for one person. The
 * organiser block reads the organiser side, a participant their participant
 * side: the score of the role they hold on this outing.
 */
export function whatWasSaidOn(
  entry: Record<string, unknown> | null | undefined,
  side: CardSide,
): WhatWasSaidOfThem | undefined {
  if (!entry || typeof entry !== 'object') {
    return undefined;
  }
  const score = entry[side];
  const count = entry[`${side}Count`];
  return {
    score: typeof score === 'number' || score === null ? score : undefined,
    count: typeof count === 'number' ? count : undefined,
    city: typeof entry['city'] === 'string' ? entry['city'] : undefined,
    form: typeof entry['form'] === 'string' ? entry['form'] : undefined,
  };
}

const initialsOf = (person: PersonOnALine): string =>
  [person.firstName, person.lastName].map((name) => (name ?? '').trim().charAt(0).toUpperCase()).join('');

export function whatAProfileLineSays(
  person: PersonOnALine,
  said: WhatWasSaidOfThem | undefined,
  myId: string | undefined,
  words: LineWords,
): ProfileLine {
  const isMe = !!person.id && !!myId && person.id === myId;
  const opens = isMe ? ['/home/profile'] : person.id ? ['/home/member', person.id] : [];
  const photo = person.avatarUrl?.trim() || undefined;
  const city = said?.city?.trim() || undefined;

  const sent = said?.count;
  const count = typeof sent === 'number' && Number.isInteger(sent) && sent >= 0 ? sent : undefined;
  const score = said?.score;
  const scored =
    typeof score === 'number' && Number.isFinite(score) && (count === undefined || count >= MIN_VOICES_FOR_A_SCORE);
  // Unrated only when the API said so: a null score, or a count under three. Silence is not « not yet ».
  const unrated = !scored && (score === null || (count !== undefined && count < MIN_VOICES_FOR_A_SCORE));
  const form: Addressed = FORMS.includes(said?.form as Addressed) ? (said?.form as Addressed) : 'neutral';

  return {
    initials: initialsOf(person),
    ...(photo ? { photo } : {}),
    ...(city ? { city } : {}),
    ...(scored ? { score: scoreLabel(score as number, words.language) } : {}),
    ...(scored && count !== undefined
      ? { reviews: words.say(count === 1 ? 'PROFILE_LINE.REVIEWS_ONE' : 'PROFILE_LINE.REVIEWS_MANY', { count }) }
      : {}),
    ...(unrated ? { notYetRated: words.say(keyFor('PROFILE_LINE.NOT_YET_RATED', form)) } : {}),
    isMe,
    opens,
  };
}
